import {
  Card as MUICard,
  CardProps as MUICardProps,
  SxProps,
  Theme,
} from '@mui/material';

import { COLORS } from '@/styles/tokens/colors';

export type CardVariant =
  | 'outlined'
  | 'filled'
  | 'filled-callout'
  | 'error'
  | 'filled-teal'
  | 'filled-outline'
  | 'filled-navy'
  | 'transparent'
  | 'outlined-highlight';

export type CardProps = Omit<MUICardProps, 'variant'> &
  React.PropsWithChildren<{
    variant: CardVariant;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- linter legacy
    inputRef?: React.Ref<any>;
  }> &
  React.HTMLProps<HTMLDivElement>;

function getVariantStyle(variant: CardProps['variant']) {
  switch (variant) {
    case 'outlined':
      // this case is handled by the default card component
      return {};
    case 'filled-callout': {
      return {
        border: `none`,
        bgcolor: COLORS.FUNCTIONAL.CALLOUT,
      };
    }
    case 'error':
      return {
        borderColor: COLORS.FUNCTIONAL.ERROR[500],
        bgcolor: COLORS.FUNCTIONAL.ERROR[50],
      };
    case 'filled-teal': {
      return {
        border: `none`,
        bgcolor: COLORS.TEAL[50],
      };
    }
    case 'filled-navy': {
      return {
        border: `none`,
        bgcolor: COLORS.NAVY[800],
      };
    }
    case 'filled-outline': {
      return {
        bgcolor: COLORS.GRAY[50],
      };
    }
    case 'filled':
      return {
        border: 'none',
        bgcolor: COLORS.GRAY[100],
      };
    case 'transparent':
      return {
        border: 'none',
        bgcolor: COLORS.PRIMITIVES.WHITE,
      };

    case 'outlined-highlight':
      return {
        borderColor: COLORS.FUNCTIONAL.WARNING[400],
      };

    default:
      throw new Error(`Unrecognized card variant ${variant}`);
  }
}

export function Card({
  variant,
  children,
  sx,
  inputRef,
  ...cardProps
}: CardProps) {
  const mergedSx: SxProps<Theme> = Object.assign(
    getVariantStyle(variant),
    sx || {}
  );
  return (
    <MUICard ref={inputRef} {...cardProps} sx={mergedSx}>
      {children}
    </MUICard>
  );
}
